import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

// Get token from sessionStorage for authentication
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

// TypeScript interfaces for API responses

// 1. Overview endpoint interfaces
export interface OverviewCounts {
  totalRestaurants: number;
  activeOrders: number;
  pendingPayments: number;
}

export interface BankDetails {
  bankName: string;
  accountName: string;
  accountNumber: number;
  bankCode: number;
  inUse: boolean;
  _id: string;
}

export interface LegalDocuments {
  cacRegistrationCertificate: string;
  foodLisence: string;
  taxIdentificationNumber: string | null;
}

export interface PendingDocument {
  documentId: string;
  restaurantId: string;
  restaurantName: string;
  legalDocuments: LegalDocuments;
  bankDetails: BankDetails;
  status: "complete" | "pending" | "approved" | "rejected";
  submissionDate: string;
  reviewDate: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecentActivityLog {
  _id: string;
  activityType: string;
  restaurantName: string;
  description: string;
  createdAt: string;
}

export interface OverviewData {
  counts: OverviewCounts;
  pendingDocuments: PendingDocument[];
  recentActivityLogs: RecentActivityLog[];
}

// 2. Pending documents endpoint interfaces - Updated to match actual API response
export interface PendingDocumentItem {
  documentId: string;
  restaurantId: string;
  restaurantName: string;
  legalDocuments: LegalDocuments;
  bankDetails: BankDetails;
  status: "pending" | "complete" | "approved" | "rejected";
  submissionDate: string;
  reviewDate?: string | null;
  reviewNotes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PendingDocumentsData {
  pendingDocuments: PendingDocumentItem[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

// 3. Activity logs endpoint interfaces
export interface ActivityLogRestaurant {
  businessInformation: {
    businessName: string;
  };
  _id: string;
}

export interface ActivityLogItem {
  _id: string;
  activityType: string;
  restaurantId: ActivityLogRestaurant;
  restaurantName: string;
  description: string;
  metadata: {
    documentType: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ActivityLogsData {
  activityLogs: ActivityLogItem[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

// 4. Review document interfaces
export interface ReviewDocumentRequest {
  action: "approve" | "reject";
  restaurantId: string;
  documentType: string;
  reviewNotes?: string;
}

export interface ReviewDocumentData {
  restaurantId: string;
  restaurantName: string;
  documentType: string;
  status: "approved" | "rejected";
  reviewDate: string;
  _id: string;
  submissionDate: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

// API Response interfaces
interface OverviewResponse {
  success: boolean;
  data: OverviewData;
}

interface PendingDocumentsResponse {
  success: boolean;
  data: {
    pendingDocuments: PendingDocumentItem[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
  };
}

interface ActivityLogsResponse {
  success: boolean;
  data: ActivityLogsData;
}

interface ReviewDocumentResponse {
  success: boolean;
  message: string;
  data: ReviewDocumentData;
}

// Redux state interface
interface RestaurantOverviewState {
  // Overview data
  overview: OverviewData | null;
  overviewStatus: "idle" | "loading" | "success" | "failed";
  overviewError: string | null;

  // Pending documents data (from dedicated endpoint)
  pendingDocuments: PendingDocumentItem[];
  pendingDocumentsStatus: "idle" | "loading" | "success" | "failed";
  pendingDocumentsError: string | null;
  pendingDocumentsTotalCount: number;
  pendingDocumentsCurrentPage: number;
  pendingDocumentsTotalPages: number;

  // Activity logs data
  activityLogs: RecentActivityLog[];
  activityLogsStatus: "idle" | "loading" | "success" | "failed";
  activityLogsError: string | null;
  activityLogsTotalCount: number;
  activityLogsCurrentPage: number;
  activityLogsTotalPages: number;

  // Review document status
  reviewDocumentStatus: "idle" | "loading" | "success" | "failed";
  reviewDocumentError: string | null;
}

const initialState: RestaurantOverviewState = {
  // Overview
  overview: null,
  overviewStatus: "idle",
  overviewError: null,

  // Pending documents
  pendingDocuments: [],
  pendingDocumentsStatus: "idle",
  pendingDocumentsError: null,
  pendingDocumentsTotalCount: 0,
  pendingDocumentsCurrentPage: 1,
  pendingDocumentsTotalPages: 1,

  // Activity logs
  activityLogs: [],
  activityLogsStatus: "idle",
  activityLogsError: null,
  activityLogsTotalCount: 0,
  activityLogsCurrentPage: 1,
  activityLogsTotalPages: 1,

  // Review document
  reviewDocumentStatus: "idle",
  reviewDocumentError: null,
};

// Async thunks for API calls

// 1. Get overview data
export const getRestaurantOverview = createAsyncThunk<
  OverviewResponse,
  void,
  { rejectValue: string }
>(
  "restaurantOverview/getRestaurantOverview",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<OverviewResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/overview`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurant overview"
      );
    }
  }
);

// 2. Get pending documents
export const getPendingDocuments = createAsyncThunk<
  PendingDocumentsResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "restaurantOverview/getPendingDocuments",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());

      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_RESTAURANT_URL}/pending-documents${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axios.get<PendingDocumentsResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Validate response structure
      if (!response.data || !response.data.data || !response.data.data.pendingDocuments) {
        console.error('Invalid pending documents API response structure:', response.data);
        return {
          success: false,
          data: {
            pendingDocuments: [],
            totalCount: 0,
            currentPage: 1,
            totalPages: 1
          }
        };
      }
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending documents"
      );
    }
  }
);

// 3. Get activity logs
export const getActivityLogs = createAsyncThunk<
  ActivityLogsResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "restaurantOverview/getActivityLogs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());

      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_RESTAURANT_URL}/activity-logs${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axios.get<ActivityLogsResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch activity logs"
      );
    }
  }
);

// 4. Review document
export const reviewDocument = createAsyncThunk<
  ReviewDocumentResponse,
  { documentId: string; reviewData: ReviewDocumentRequest },
  { rejectValue: string }
>(
  "restaurantOverview/reviewDocument",
  async ({ documentId, reviewData }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.put<ReviewDocumentResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/documents/${documentId}/review`,
        reviewData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to review document"
      );
    }
  }
);

// Redux slice
const restaurantOverviewSlice = createSlice({
  name: "restaurantOverview",
  initialState,
  reducers: {
    clearOverviewErrors: (state) => {
      state.overviewError = null;
      state.pendingDocumentsError = null;
      state.activityLogsError = null;
      state.reviewDocumentError = null;
    },
    setPendingDocumentsPage: (state, action: PayloadAction<number>) => {
      state.pendingDocumentsCurrentPage = action.payload;
    },
    setActivityLogsPage: (state, action: PayloadAction<number>) => {
      state.activityLogsCurrentPage = action.payload;
    },
    resetReviewDocumentStatus: (state) => {
      state.reviewDocumentStatus = "idle";
      state.reviewDocumentError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get overview
      .addCase(getRestaurantOverview.pending, (state) => {
        state.overviewStatus = "loading";
        state.overviewError = null;
      })
      .addCase(getRestaurantOverview.fulfilled, (state, action) => {
        state.overviewStatus = "success";
        state.overview = action.payload.data;
        
        // Also populate the pending documents and activity logs from the overview response
        // since the API returns all data together
        if (action.payload.data.pendingDocuments) {
          state.pendingDocuments = action.payload.data.pendingDocuments;
          state.pendingDocumentsTotalCount = action.payload.data.pendingDocuments.length;
          state.pendingDocumentsStatus = "success";
        }
        
        if (action.payload.data.recentActivityLogs) {
          // Convert RecentActivityLog to match the expected format
          state.activityLogs = action.payload.data.recentActivityLogs;
          state.activityLogsTotalCount = action.payload.data.recentActivityLogs.length;
          state.activityLogsStatus = "success";
        }
      })
      .addCase(getRestaurantOverview.rejected, (state, action) => {
        state.overviewStatus = "failed";
        state.overviewError = action.payload || "Failed to fetch overview";
      })

      // Get pending documents
      .addCase(getPendingDocuments.pending, (state) => {
        state.pendingDocumentsStatus = "loading";
        state.pendingDocumentsError = null;
      })
      .addCase(getPendingDocuments.fulfilled, (state, action) => {
        state.pendingDocumentsStatus = "success";
        state.pendingDocuments = action.payload.data.pendingDocuments;
        state.pendingDocumentsTotalCount = action.payload.data.totalCount;
        state.pendingDocumentsCurrentPage = action.payload.data.currentPage;
        state.pendingDocumentsTotalPages = action.payload.data.totalPages;
      })
      .addCase(getPendingDocuments.rejected, (state, action) => {
        state.pendingDocumentsStatus = "failed";
        state.pendingDocumentsError =
          action.payload || "Failed to fetch pending documents";
      })

      // Get activity logs
      .addCase(getActivityLogs.pending, (state) => {
        state.activityLogsStatus = "loading";
        state.activityLogsError = null;
      })
      .addCase(getActivityLogs.fulfilled, (state, action) => {
        state.activityLogsStatus = "success";
        state.activityLogs = action.payload.data.activityLogs;
        state.activityLogsTotalCount = action.payload.data.totalCount;
        state.activityLogsCurrentPage = action.payload.data.currentPage;
        state.activityLogsTotalPages = action.payload.data.totalPages;
      })
      .addCase(getActivityLogs.rejected, (state, action) => {
        state.activityLogsStatus = "failed";
        state.activityLogsError =
          action.payload || "Failed to fetch activity logs";
      })

      // Review document
      .addCase(reviewDocument.pending, (state) => {
        state.reviewDocumentStatus = "loading";
        state.reviewDocumentError = null;
      })
      .addCase(reviewDocument.fulfilled, (state, action) => {
        state.reviewDocumentStatus = "success";
        // Optionally update the pending documents list by removing the reviewed document
        const reviewedDocumentId = action.payload.data._id;
        state.pendingDocuments = state.pendingDocuments.filter(
          (doc) => doc.documentId !== reviewedDocumentId
        );
      })
      .addCase(reviewDocument.rejected, (state, action) => {
        state.reviewDocumentStatus = "failed";
        state.reviewDocumentError =
          action.payload || "Failed to review document";
      });
  },
});

export const {
  clearOverviewErrors,
  setPendingDocumentsPage,
  setActivityLogsPage,
  resetReviewDocumentStatus,
} = restaurantOverviewSlice.actions;

export default restaurantOverviewSlice.reducer;
